import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  formatDate,
  INDUSTRY_VOICES_THEME,
} from '../../config';

function toMs(value) {
  if (!value) return 0;
  if (typeof value === 'string') return new Date(value).getTime() || 0;
  if (value?.toMillis) return value.toMillis();
  try {
    return new Date(value).getTime() || 0;
  } catch {
    return 0;
  }
}

function normaliseToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isIndustryVoices(post) {
  const want = normaliseToken(INDUSTRY_VOICES_THEME);
  const category = normaliseToken(post?.category);
  if (category === want) return true;
  const themes = Array.isArray(post?.themes) ? post.themes : [];
  return themes.some((t) => normaliseToken(t) === want);
}

function IndustryVoicesWidget() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState(() => ({}));

  const markLoaded = (key) => {
    if (!key) return;
    setLoadedImages((prev) => {
      if (prev[key]) return prev;
      return { ...prev, [key]: true };
    });
  };

  useEffect(() => {
    // Pull more than we show so unpublished drafts don't crowd out the widget.
    const q = query(collection(db, 'posts'), orderBy('publishedAt', 'desc'), limit(250));

    const unsub = onSnapshot(
      q,
      (snap) => {
        // Avoid rendering stale cached snapshots on refresh (causes brief fallback-image flash)
        if (snap.metadata?.fromCache) return;
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPosts(items);
        setLoading(false);
      },
      () => {
        setPosts([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const voices = useMemo(() => {
    return [...posts]
      .filter((p) => p && p.published !== false)
      .filter(isIndustryVoices)
      .sort((a, b) => toMs(b.publishedAt) - toMs(a.publishedAt));
  }, [posts]);

  const featured = voices[0];
  const rightItems = voices.slice(1, 3);

  return (
    <section className="content-card industry-voices" aria-label="Industry voices">
      <div className="industry-voices-header">
        <h2 className="industry-voices-heading">Industry voices</h2>
        <Link to="/industry-voices" className="industry-voices-see-more">
          See more →
        </Link>
      </div>

      {loading && !posts.length && (
        <div className="industry-voices-body">
          <p className="secondary-text">Loading Industry Voices…</p>
        </div>
      )}

      {!loading && !voices.length && (
        <div className="industry-voices-body">
          <p className="secondary-text">
            No Industry Voices posts yet. Add the theme <strong>{INDUSTRY_VOICES_THEME}</strong> to a post in the admin editor.
          </p>
        </div>
      )}

      {!!featured && (
        <div className="industry-voices-grid">
          <Link to={`/news/${featured.slug}`} className="voices-link voices-featured-link">
            <article
              className="voices-featured"
              data-loaded={
                loadedImages[`voices-featured:${featured.imageThumbUrl || featured.imageUrl || ''}`]
                  ? 'true'
                  : 'false'
              }
            >
              {(() => {
                const imgSrc = featured.imageThumbUrl || featured.imageUrl;
                return (
                  imgSrc && (
                    <img
                      key={imgSrc}
                      src={imgSrc}
                      alt={featured.title}
                      className="voices-featured-image"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      decoding="async"
                      fetchPriority="high"
                      onLoad={() => markLoaded(`voices-featured:${imgSrc}`)}
                      onError={(e) => {
                        const target = e.target;
                        // eslint-disable-next-line no-param-reassign
                        target.style.display = 'none';
                      }}
                    />
                  )
                );
              })()}

              <div className="voices-featured-body">
                <div className="voices-meta-row">
                  <span className="voices-category">{featured.category}</span>
                  <span className="dot" aria-hidden="true" />
                  <span className="voices-theme">{INDUSTRY_VOICES_THEME}</span>
                  {featured.publishedAt && (
                    <>
                      <span className="dot" aria-hidden="true" />
                      <span className="voices-date">{formatDate(featured.publishedAt)}</span>
                    </>
                  )}
                </div>
                <div className="voices-featured-title">{featured.title}</div>
                {featured.excerpt && <div className="voices-featured-excerpt">{featured.excerpt}</div>}
              </div>
            </article>
          </Link>

          <div className="industry-voices-right" aria-label="More Industry Voices">
            {rightItems.map((item) => {
              const imgSrc = item.imageThumbUrl || item.imageUrl;
              return (
                <Link
                  key={item.id || item.slug}
                  to={`/news/${item.slug || item.id}`}
                  className="voices-link"
                >
                  <article className="voices-card">
                    {imgSrc && (
                      <div
                        className="voices-card-media"
                        data-loaded={loadedImages[`voices-card:${imgSrc}`] ? 'true' : 'false'}
                        style={{
                          '--lqip-bg': item.imageLqip ? `url("${item.imageLqip}")` : undefined,
                        }}
                      >
                        <img
                          key={imgSrc}
                          src={imgSrc}
                          alt={item.title}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          decoding="async"
                          onLoad={() => markLoaded(`voices-card:${imgSrc}`)}
                          onError={(e) => {
                            const target = e.target;
                            // eslint-disable-next-line no-param-reassign
                            target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="voices-card-body">
                      <div className="voices-meta-row">
                        <span className="voices-category">{item.category}</span>
                        <span className="dot" aria-hidden="true" />
                        <span className="voices-theme">{INDUSTRY_VOICES_THEME}</span>
                      </div>
                      <div className="voices-card-title">{item.title}</div>
                      {item.publishedAt && (
                        <div className="secondary-text">
                          <time dateTime={String(item.publishedAt)}>{formatDate(item.publishedAt)}</time>
                        </div>
                      )}
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default IndustryVoicesWidget;


